import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { updateUserDetails } from 'src/utils/urls';

import { createNewUser, NewUserReturnType } from '../types/createNewUser';

import { useAuth } from 'src/hooks';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type ApiResponse<TData = unknown> = {
  Data?: TData;
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = ApiResponse<NewUserReturnType>;

type TError = Error;

type TVariables = createNewUser;

type TArguments = {
  token: string;
  tokenType: string;
  data: createNewUser;
  id: string;
};

async function updateUser({
  token,
  tokenType,
  data,
  id,
}: TArguments): Promise<TResult> {
  const response = await fetch(updateUserDetails(id), {
    method: 'PUT',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  let result: TResult | null = null;

  try {
    const text = await response.text();
    result = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!result) {
    throw new Error('Invalid server response');
  }

  return result;
}

function useUpdateUser(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  unknown
> {
  const queryClient = useQueryClient();
  const { token, tokenType } = useAuth();

  return useMutation(async (params: TVariables) => {
    const result = await updateUser({
      token,
      tokenType,
      data: params,
      id: params.Id,
    });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to update user';

      throw new Error(errorMessage);
    }

    queryClient.invalidateQueries(['get_users_list']);

    return result;
  });
}

export { useUpdateUser };
